import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {ActionEvent, ButtonOption, CloseEvent, FocusItemEvent, UnfocusItemEvent} from './types';
import css from './index.styl?inline';
import template from './index.jade';

const MAX_BUTTONS = 3;

export interface PopoverCardJsxProps {
  attrs?: {
    'button-options': ButtonOption[];
    'is-segmented': boolean;
    title: string;
    subtitle: string;
  };
}
export class PopoverCard extends Component<EmptyObj, PopoverCardJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<PopoverCardJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      title: {type: `string`},
      subtitle: {type: `string`},
      'button-options': {type: `json`, description: `interface: ButtonOption[]`},
      'is-segmented': `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      helpers: {
        handleSelect: (item) => () => {
          this.unfocusItem();
          this.dispatchEvent(new CustomEvent(`action`, {detail: {item}}) as ActionEvent);
        },
        handleChange: (ev) => {
          const {item} = ev.detail;
          this.helpers.handleSelect(item)();
        },
        handleFocusItem: (buttonOption) => (ev) => {
          const item = buttonOption || ev.detail.item;
          this.dispatchEvent(
            new CustomEvent(`popoverFocusItem`, {
              detail: {item},
              composed: true,
              bubbles: true,
            }) as FocusItemEvent,
          );
        },
        handleUnfocusItem: (ev) => {
          const {isOpen} = ev.detail;
          if (!isOpen) {
            this.unfocusItem();
          }
        },
        handleClose: () => {
          this.dispatchEvent(new CustomEvent(`close`) as CloseEvent);
        },
        hasOverflow: () => {
          return this.attr(`button-options`).length > MAX_BUTTONS;
        },
        getMainOptions: () => {
          return this.attr(`button-options`).slice(0, MAX_BUTTONS);
        },
        getOverflowOptions: () => {
          return this.attr(`button-options`).slice(MAX_BUTTONS);
        },
      },
    };
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `button-options`) {
      this.validate();
    }
  }

  unfocusItem() {
    this.dispatchEvent(
      new CustomEvent(`popoverUnfocusItem`, {
        composed: true,
        bubbles: true,
      }) as UnfocusItemEvent,
    );
  }

  validate() {
    // Overflow items (items that show in ... menu) cannot have child values
    if (this.helpers.getOverflowOptions().some((option) => Object.prototype.hasOwnProperty.call(option, `values`))) {
      throw new Error(`Overflow buttons cannot have multiple values`);
    }
  }
}

export const PopoverCardEl = defineAndMakeJsxFactory<PopoverCardJsxProps>(`mp-popover-card`, PopoverCard);
